import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { format } from "date-fns";
import { GeminiService } from '@/services/geminiService';
import { GoogleDriveService } from '@/services/googleDriveService';
import { useNetworkStatus } from '@/hooks/useOfflineReport';
import { useSupabaseReports } from '@/hooks/useSupabaseReports';
import { useSupabaseUsers } from '@/hooks/useSupabaseUsers';
import { useSupabasePeriodNavigation } from '@/hooks/useSupabasePeriods';
import { useReportGroupsCompatible } from '@/hooks/useSupabaseReportGroupsOnly';
import { ReportFooter } from '@/components/ReportFooter';
import ReportGroupsSidebar from '@/components/ReportGroupsSidebar';
import { ReportListView } from '@/components/ReportListView';
import { useCacheInvalidation } from '@/services/cacheInvalidationService';
import { Button } from "@/components/ui/button";
import { useAuth } from '@/components/AuthProvider';
import { supabase } from '@/integrations/supabase/client';

// Components đã được tách ra thành ReportGroupsSidebar và ReportListView

const Index = () => {
  // 🔍 DEBUGGING: Track Index component re-renders
  const indexRenderCount = useRef(0);
  indexRenderCount.current++;
  console.log(`🏠 Index component render #${indexRenderCount.current}`);

  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();

  // States
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [expandedPeriods, setExpandedPeriods] = useState(new Set<string>());
  const [summarizingReports, setSummarizingReports] = useState(new Set());
  const [refreshingReports, setRefreshingReports] = useState(new Set());
  // Không cần state reportGroups nữa, dùng hook trực tiếp
  const [periodPage, setPeriodPage] = useState(1);
  const [isGroupSheetOpen, setIsGroupSheetOpen] = useState(false);
  const isMobile = useIsMobile();
  const [isDrawerOpen, setIsDrawerOpen] = useState(!isMobile); // Desktop mở mặc định, mobile đóng
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false);
  const { toast } = useToast();
  
  // Cache invalidation service
  const cacheInvalidation = useCacheInvalidation();

  // Hooks để lấy data từ Supabase và SQL Server
  const { 
    data: reportsResponse, 
    isLoading: reportsLoading, 
    error: reportsError,
    refetch: refetchReports 
  } = useSupabaseReports({}, { limit: 1000 });

  const { 
    data: usersResponse, 
    isLoading: usersLoading 
  } = useSupabaseUsers();

  const { 
    data: periodsResponse,
    isLoading: periodsLoading, 
    error: periodsError,
    refetch: refreshPeriods
  } = useSupabasePeriodNavigation(periodPage, 4);

  // Load report groups từ Supabase (không fallback SQL Server)
  const { 
    reportGroups, 
    loading: reportGroupsLoading, 
    error: reportGroupsError,
    refresh: refetchReportGroups
  } = useReportGroupsCompatible();

  // Extract periods and pagination from TimePeriods-based navigation
  const periods = periodsResponse?.data || [];
  const periodPagination = periodsResponse?.pagination || null;

  // Pagination handlers using TimePeriods
  const nextPage = useCallback(async () => {
    if (periodPagination?.hasNextPage) {
      setPeriodPage(prev => prev + 1);
    }
  }, [periodPagination?.hasNextPage]);

  const prevPage = useCallback(async () => {
    if (periodPagination?.hasPrevPage) {
      setPeriodPage(prev => prev - 1);
    }
  }, [periodPagination?.hasPrevPage]);

  const goToPage = useCallback(async (newPage: number) => {
    if (newPage >= 1 && (!periodPagination?.totalPages || newPage <= periodPagination.totalPages)) {
      setPeriodPage(newPage);
    }
  }, [periodPagination?.totalPages]);

  // Extract data from responses
  const reports = reportsResponse?.data || [];
  const users = usersResponse || []; // usersResponse is already array from useSupabaseUsers
  const loading = reportsLoading || usersLoading || periodsLoading || reportGroupsLoading;
  const error = reportsError || periodsError || reportGroupsError;

  const formatDate = useCallback((date) => {
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return format(dateObj, 'dd/MM/yyyy');
    } catch {
      return 'N/A';
    }
  }, []);

  const { isOnline } = useNetworkStatus();
  const openFileUrl = useCallback((url) => {
    if (!url) return;
    if (isOnline) {
      window.open(url, '_blank');
    } else {
      toast({
        title: 'Không có mạng',
        description: 'Bạn đang offline. Chỉ có thể mở báo cáo đã được lưu offline (bấm nút lưu trên mỗi báo cáo).',
        variant: 'destructive',
      });
    }
  }, [isOnline, toast]);

  // Function to handle report refresh
  const handleRefreshReport = useCallback(async (reportId) => {
    try {
      setRefreshingReports(prev => new Set(prev).add(reportId));
      
      const report = reports.find(r => r.id === reportId || r.legacy_id === reportId);
      if (!report) {
        console.error('Could not find report with ID:', reportId);
        console.error('Available reports:', reports.map(r => ({ id: r.id, legacy_id: r.legacy_id, name: r.report_name })));
        throw new Error('Không tìm thấy báo cáo');
      }

      if (!report.full_url) {
        throw new Error('Không có FullURL để tóm tắt báo cáo');
      }

      // Getting report content for AI summarization
      
      // Use direct fetch method only
      const fileContent = await GoogleDriveService.getFileContentFromShareLink(report.full_url);
      
      // Generate summary using Gemini AI
      const summary = await GeminiService.summarizeDocument(fileContent, report.report_name);
      
      // Refresh reports from Supabase instead of updating locally
      await refetchReports();

      toast({
        title: "Thành công",
        description: "Đã tóm tắt báo cáo bằng AI",
      });

    } catch (error) {
      console.error('Error summarizing report:', error);
      toast({
        title: "Lỗi",
        description: error.message || "Không thể tóm tắt báo cáo",
        variant: "destructive",
      });
    } finally {
      setRefreshingReports(prev => {
        const newSet = new Set(prev);
        newSet.delete(reportId);
        return newSet;
      });
    }
  }, [reports, refetchReports, toast]);

  // Function to get user information - memoized
  // Matches by legacy_id, auth_user_id, or report's auth_user_id
  const getUserInfo = useCallback((userId?: string | null, authUserId?: string | null) => {
    const user = users.find(u =>
      (userId && (u.legacy_id === userId || u.auth_user_id === userId)) ||
      (authUserId && u.auth_user_id === authUserId)
    );
    return user ? {
      name: user.user_name || 'Unknown',
      email: user.email || ''
    } : { name: 'Unknown', email: '' };
  }, [users]);

  const handleDeleteReport = useCallback(async (reportId: string) => {
    const { error } = await supabase.from('reports').delete().eq('id', reportId);
    if (error) {
      toast({ title: 'Xóa thất bại', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Đã xóa báo cáo' });
      queryClient.invalidateQueries({ queryKey: ['supabase-reports'] });
    }
  }, [toast, queryClient]);

  // Function to handle refresh data
  const handleRefreshWithSync = useCallback(async () => {
    setIsRefreshing(true);

    try {
      cacheInvalidation.invalidateAllReportsData();

      await Promise.all([
        refetchReports(),
        refreshPeriods(),
        refetchReportGroups()
      ]);

      toast({
        title: "Hoàn thành!",
        description: "Dữ liệu đã được cập nhật thành công",
      });
    } catch (error) {
      console.error('Lỗi khi làm mới dữ liệu:', error);
      toast({
        variant: "destructive",
        title: "Lỗi làm mới",
        description: "Có lỗi xảy ra khi cập nhật dữ liệu",
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [refetchReports, refreshPeriods, toast]);

  // Function to group reports by Period
  const groupReportsByPeriod = (reports) => {
    const grouped = {};
    reports.forEach(report => {
      const period = report.period || 'No Period';
      if (!grouped[period]) {
        grouped[period] = [];
      }
      grouped[period].push(report);
    });
    return grouped;
  };

  // Function to toggle row expansion - memoized để tránh re-render
  const toggleRowExpansion = useCallback((reportId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reportId)) {
        newSet.delete(reportId);
      } else {
        newSet.add(reportId);
      }
      return newSet;
    });
  }, []);

  // Function to toggle period expansion - memoized để tránh re-render
  const togglePeriodExpansion = useCallback((period) => {
    console.log(`🔄 Index: togglePeriodExpansion called for period: ${period}`);
    setExpandedPeriods(prev => {
      const newSet = new Set(prev);
      const action = newSet.has(period) ? 'collapse' : 'expand';
      console.log(`📂 ${action} period: ${period}`);
      
      if (newSet.has(period)) {
        newSet.delete(period);
      } else {
        newSet.add(period);
      }
      return newSet;
    });
  }, []);

  // Server-side pagination handlers
  const handleNextPage = useCallback(() => {
    if (periodPagination?.hasNextPage) {
      nextPage();
    }
  }, [periodPagination, nextPage]);

  const handlePrevPage = useCallback(() => {
    if (periodPagination?.hasPrevPage) {
      prevPage();
    }
  }, [periodPagination, prevPage]);

  const handlePageChange = useCallback((page: number) => {
    goToPage(page);
  }, [goToPage]);

  // Handle mobile group selection with auto-hide
  const handleMobileGroupSelect = useCallback((groupId: string) => {
    setActiveTab(groupId);
    if (isMobile) {
      setIsGroupSheetOpen(false); // Auto-hide sheet on mobile
    }
  }, [isMobile]);

  // Handle drawer toggle
  const handleDrawerToggle = useCallback(() => {
    if (isMobile) {
      setIsGroupSheetOpen(!isGroupSheetOpen);
    } else {
      setIsDrawerOpen(!isDrawerOpen);
    }
  }, [isMobile, isGroupSheetOpen, isDrawerOpen]);

  // Handle drawer close
  const handleDrawerClose = useCallback(() => {
    if (isMobile) {
      setIsGroupSheetOpen(false);
    } else {
      setIsDrawerOpen(false);
    }
  }, [isMobile]);

  // Period-level pagination handlers (client-side for UI grouping)
  const handlePeriodNextPage = useCallback(() => {
    setPeriodPage(prev => prev + 1);
  }, []);

  const handlePeriodPrevPage = useCallback(() => {
    if (periodPage > 1) {
      setPeriodPage(prev => prev - 1);
    }
  }, [periodPage]);

  const handleTabChange = useCallback((newTab: string) => {
    setActiveTab(newTab);
  }, []);

  // Memoize drawer state để prevent unnecessary re-render
  const drawerIsOpen = useMemo(() => 
    isMobile ? isGroupSheetOpen : isDrawerOpen
  , [isMobile, isGroupSheetOpen, isDrawerOpen]);

  // Memoize refreshing state để prevent unnecessary re-render
  const isRefreshingState = useMemo(() => 
    isRefreshing || reportGroupsLoading
  , [isRefreshing, reportGroupsLoading]);



  const handlePeriodPageChange = useCallback((page: number) => {
    setPeriodPage(page);
  }, []);

  // Reset period page chỉ khi search (không reset khi chỉ thay đổi tab)
  useEffect(() => {
    setPeriodPage(1);
  }, [searchTerm]);

  // Helper functions đã được tách ra trong ReportGroupsSidebar component

  // Memoized grouped reports
  const groupedReports = useMemo(() => {
    if (!reports.length || !reportGroups.length) return {};
    
    const grouped = {};
    
    reportGroups.forEach(group => {
      const groupId = group.ID || group.GroupID || group.id;
      if (groupId) {
        const groupReports = reports.filter(report => report.id_group === groupId);
        grouped[groupId] = {
          ...group,
          ID: groupId,
          GroupID: groupId,
          reports: groupReports
        };
        
        // DEBUG: Log group "BÁO CÁO THÁNG"
        if (group.GroupName === 'BÁO CÁO THÁNG') {
          const periodsBreakdown = groupReports.reduce((acc, r) => {
            acc[r.period || 'null'] = (acc[r.period || 'null'] || 0) + 1;
            return acc;
          }, {});
          const hasW2540 = groupReports.some(r => r.period === 'W2540');
          const w2540Reports = groupReports.filter(r => r.period === 'W2540');
          
          console.log('🔍 DEBUG "BÁO CÁO THÁNG":');
          console.log('  - Group ID:', groupId);
          console.log('  - Total reports in group:', groupReports.length);
          console.log('  - Has W2540:', hasW2540);
          console.log('  - W2540 reports count:', w2540Reports.length);
          console.log('  - Periods breakdown:', JSON.stringify(periodsBreakdown, null, 2));
          if (w2540Reports.length > 0) {
            console.log('  - W2540 Reports:');
            w2540Reports.forEach(r => {
              console.log(`    * ${r.report_name} (ID: ${r.id})`);
            });
          }
        }
      }
    });
    
    return grouped;
  }, [reports, reportGroups]);

  // 🎯 ENHANCED: Memoized sorted report groups by STT with STABLE reference  
  const sortedReportGroups = useMemo(() => {
    const groups = Object.values(groupedReports);
    const sorted = [...groups].sort((a: any, b: any) => {
      const sttA = a.STT || 0;
      const sttB = b.STT || 0;
      return sttA - sttB;
    });
    
    console.log('🔧 sortedReportGroups recomputed with stable dependencies:', {
      groupCount: sorted.length,
      groupIds: sorted.map((g: any) => g.ID || g.GroupID || g.id)
    });
    
    return sorted;
  }, [
    // 🔧 STABLE DEPENDENCY: Use serialized keys instead of object reference
    Object.keys(groupedReports).sort().join(','),
    Object.values(groupedReports).map((g: any) => `${g.ID || g.GroupID || g.id}_${g.STT || 0}_${g.reports?.length || 0}`).sort().join(',')
  ]);



  // Memoized filtered reports for search
  const filteredReports = useMemo(() => {
    if (!searchTerm) return reports;
    
    const searchLower = searchTerm.toLowerCase();
    
    return reports.filter(report => {
      // Tìm theo tên báo cáo
      const matchReportName = report.report_name?.toLowerCase().includes(searchLower);
      
      // Tìm theo period
      const matchPeriod = report.period?.toLowerCase().includes(searchLower);
      
      // Tìm theo SummaryByAI
      const matchSummary = report.summary_by_ai?.toLowerCase().includes(searchLower);
      
      // Tìm theo người tạo (tên và email)
      const userInfo = getUserInfo(report.created_by_user_id, report.auth_user_id);
      const matchUserName = userInfo.name?.toLowerCase().includes(searchLower);
      const matchUserEmail = userInfo.email?.toLowerCase().includes(searchLower);
      
      return matchReportName || matchPeriod || matchSummary || matchUserName || matchUserEmail;
    });
  }, [reports, searchTerm, getUserInfo]);

  // Period-based pagination - periods are already paginated from server
  const paginatedPeriods = useMemo(() => {
    // ✅ FIX: Chỉ lấy reports của 4 periods hiện tại (page hiện tại)
    const currentPeriods = periods
      .map(p => p.Period)
      .filter(p => p !== null && p !== undefined); // Filter out null/undefined periods
    const currentPageReports = filteredReports.filter(report => 
      report.period && currentPeriods.includes(report.period) // Check report.period is not null
    );
    
    const groupedData = groupReportsByPeriod(currentPageReports);
    
    return {
      data: groupedData,
      totalPages: periodPagination?.totalPages || 1,
      totalPeriods: periods.length, // Chỉ 4 periods hiện tại
      currentPage: periodPagination?.page || 1,
      hasNextPage: periodPagination?.hasNextPage || false,
      hasPrevPage: periodPagination?.hasPrevPage || false
    };
  }, [filteredReports, periodPagination, periods]);

  // Group periods pagination - simplified for period-based approach  
  const paginatedGroupPeriods = useMemo(() => {
    if (activeTab === 'all') return { data: {}, totalPages: 0, totalPeriods: 0, currentPage: 1, hasNextPage: false, hasPrevPage: false };
    
    const selectedGroup = Object.values(groupedReports).find((group: any) => 
      (group.ID || group.id || '').toString() === activeTab
    ) as any;
    
    if (!selectedGroup) return { data: {}, totalPages: 0, totalPeriods: 0, currentPage: 1, hasNextPage: false, hasPrevPage: false };
    
    // ✅ FIX: Chỉ lấy reports của 4 periods hiện tại cho group này
    const currentPeriods = periods
      .map(p => p.Period)
      .filter(p => p !== null && p !== undefined); // Filter out null/undefined periods
    
    // DEBUG: Log filtering for "BÁO CÁO THÁNG"
    if (activeTab === '131CBB23-FB62A828') {
      console.log('🔍 paginatedGroupPeriods FILTER DEBUG:');
      console.log('  - Current periods:', JSON.stringify(currentPeriods));
      console.log('  - Total reports in group:', selectedGroup.reports?.length);
      console.log('  - Periods detail:');
      periods.forEach((p, idx) => {
        console.log(`    [${idx}] Period: "${p.Period}" (isNull: ${p.Period === null})`);
      });
    }
    
    const filteredGroupReports = selectedGroup.reports?.filter((report: any) => {
      // Chỉ lấy reports thuộc periods hiện tại
      const shouldInclude = report.period && currentPeriods.includes(report.period);
      
      // DEBUG: Log for W2540 reports
      if (activeTab === '131CBB23-FB62A828' && report.period === 'W2540') {
        console.log('🔍 W2540 Report filter:');
        console.log('  - Name:', report.report_name);
        console.log('  - Period:', report.period);
        console.log('  - In currentPeriods?:', currentPeriods.includes(report.period));
        console.log('  - Should include?:', shouldInclude);
        console.log('  - Current periods array:', JSON.stringify(currentPeriods));
      }
      
      if (!shouldInclude) return false;
      
      if (!searchTerm) return true;
      
      const searchLower = searchTerm.toLowerCase();
      
      // Search across all fields
      const matchReportName = report.report_name?.toLowerCase().includes(searchLower);
      const matchPeriod = report.period?.toLowerCase().includes(searchLower);
      const matchSummary = report.summary_by_ai?.toLowerCase().includes(searchLower);
      
      const userInfo = getUserInfo(report.created_by_user_id, report.auth_user_id);
      const matchUserName = userInfo.name?.toLowerCase().includes(searchLower);
      const matchUserEmail = userInfo.email?.toLowerCase().includes(searchLower);
      
      return matchReportName || matchPeriod || matchSummary || matchUserName || matchUserEmail;
    }) || [];
    
    const groupedData = groupReportsByPeriod(filteredGroupReports);
    
    return {
      data: groupedData,
      totalPages: periodPagination?.totalPages || 1,
      totalPeriods: periods.length, // Chỉ 4 periods hiện tại
      currentPage: periodPagination?.page || 1,
      hasNextPage: periodPagination?.hasNextPage || false,
      hasPrevPage: periodPagination?.hasPrevPage || false
    };
  }, [
    // 🔧 STABLE DEPENDENCIES: Use stable serialized data  
    Object.keys(groupedReports).sort().join(','),
    Object.values(groupedReports).map((g: any) => `${g.ID || g.GroupID || g.id}_${g.reports?.length || 0}`).sort().join(','),
    activeTab, 
    searchTerm, 
    periodPagination?.page,
    periodPagination?.totalPages,
    periods.map(p => p.Period).sort().join(',')
  ]);

  // 🎯 ENHANCED: Optimized current page report counts với stable comparison
  const currentPageReportCounts = useMemo(() => {
    const currentPeriods = periods
      .map(p => p.Period)
      .filter(p => p !== null && p !== undefined); // Filter out null/undefined periods
    const currentPageReports = filteredReports.filter(report => 
      currentPeriods.includes(report.period)
    );
    
    // Total reports for current page
    const totalCurrentReports = currentPageReports.length;
    
    // Reports count by group for current page
    const groupCounts: { [key: string]: number } = {};
    Object.values(groupedReports).forEach((group: any) => {
      const groupId = (group.ID || group.GroupID || group.id || '').toString();
      const groupCurrentReports = group.reports?.filter((report: any) => 
        report.period && currentPeriods.includes(report.period) // Also check report.period is not null
      ) || [];
      groupCounts[groupId] = groupCurrentReports.length;
    });
    
    const result = {
      total: totalCurrentReports,
      byGroup: groupCounts
    };
    
    console.log('📊 currentPageReportCounts recomputed:', result);
    console.log('📊 Current periods (filtered):', currentPeriods);
    console.log('📊 Filtered reports count:', filteredReports.length);
    console.log('📊 Current page reports count:', currentPageReports.length);
    console.log('📊 currentPageReportCounts dependencies:', {
      filteredReportsLength: filteredReports.length,
      periodsLength: periods.length,
      groupedReportsKeys: Object.keys(groupedReports),
      expandedPeriodsSnapshot: Array.from(expandedPeriods) // 🔍 DEBUGGING: Check if this affects calculation
    });
    return result;
  }, [
    // 🔧 STABLE DEPENDENCIES: Use stable serialized data instead of object reference
    filteredReports.length,
    filteredReports.map(r => `${r.id}_${r.period}_${r.id_group}`).sort().join(','),
    periods.map(p => p.Period).sort().join(','),
    Object.keys(groupedReports).sort().join(','),
    Object.values(groupedReports).map((g: any) => `${g.ID || g.GroupID || g.id}_${g.reports?.length || 0}`).sort().join(',')
  ]);

  // 🎯 OPTIMIZED: Sidebar props with smart re-render control
  const stableSidebarProps = useMemo(() => {
    const props = {
      reportGroups: sortedReportGroups,
      activeTab,
      currentPageReportCounts,
      isDrawerOpen: drawerIsOpen,
      onTabChange: handleTabChange,
      onDrawerClose: handleDrawerClose
    };
    
    console.log('🔄 stableSidebarProps recomputed:', {
      sortedReportGroupsLength: sortedReportGroups?.length || 0,
      activeTab,
      currentPageReportCounts,
      isDrawerOpen: drawerIsOpen,
      timestamp: new Date().toISOString().slice(11, 23),
      expandedPeriodsSnapshot: Array.from(expandedPeriods), // 🔍 DEBUGGING: Check if this affects sidebar
      handleTabChangeRef: handleTabChange?.toString?.().slice(0, 100), // Function reference check
      handleDrawerCloseRef: handleDrawerClose?.toString?.().slice(0, 100)
    });
    
    return props;
  }, [sortedReportGroups, activeTab, currentPageReportCounts, drawerIsOpen, handleTabChange, handleDrawerClose]);

  // 🎯 ENHANCED: Memoize ReportListView props separately to avoid coupling
  const reportListViewProps = useMemo(() => ({
    activeTab,
    searchTerm,
    expandedRows,
    expandedPeriods,
    refreshingReports,
    paginatedPeriods,
    paginatedGroupPeriods,
    groupedReports,
    onSearchChange: setSearchTerm,
    onToggleRowExpansion: toggleRowExpansion,
    onTogglePeriodExpansion: togglePeriodExpansion,
    onOpenFile: openFileUrl,
    onSummarizeReport: handleRefreshReport,
    onDrawerToggle: handleDrawerToggle,
    onDeleteReport: handleDeleteReport,
    currentUserId: currentUser?.id ?? null,
    getUserInfo,
    formatDate
  }), [
    activeTab, searchTerm, expandedRows, expandedPeriods, refreshingReports,
    paginatedPeriods, paginatedGroupPeriods, groupedReports,
    toggleRowExpansion, togglePeriodExpansion, openFileUrl,
    handleRefreshReport, handleDrawerToggle, handleDeleteReport,
    currentUser?.id, getUserInfo, formatDate
  ]);

  // Auto-expand first period on initial data load only
  useEffect(() => {
    const allPeriods = activeTab === 'all'
      ? Object.keys(paginatedPeriods.data)
      : Object.keys(paginatedGroupPeriods.data);

    if (allPeriods.length > 0 && !hasAutoExpanded) {
      setExpandedPeriods(prev => {
        const next = new Set(prev);
        next.add(allPeriods[0]);
        return next;
      });
      setHasAutoExpanded(true);
    }
  }, [paginatedPeriods.data, paginatedGroupPeriods.data, activeTab, hasAutoExpanded]);

  // 🔍 DEBUGGING: Track activeTab changes
  useEffect(() => {
    console.log(`🏷️ Index: activeTab changed to: ${activeTab}`);
  }, [activeTab]);

  // 🔍 DEBUGGING: Track expandedPeriods changes
  useEffect(() => {
    console.log(`📂 Index: expandedPeriods changed:`, Array.from(expandedPeriods));
  }, [expandedPeriods]);

  // Reset auto-expand chỉ khi search (user intent to refresh)
  useEffect(() => {
    setHasAutoExpanded(false);
  }, [searchTerm]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <Skeleton className="h-12 w-96 mb-4" />
            <Skeleton className="h-6 w-64" />
          </div>
          
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto">
          <Alert variant="destructive" className="mt-20">
            <AlertDescription className="flex items-center justify-between">
              <span>Lỗi: {error instanceof Error ? error.message : String(error)}</span>
              <Button 
                onClick={handleRefreshWithSync}
                disabled={isRefreshing}
                variant="outline"
                size="sm"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Đang tải...' : 'Thử lại'}
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-background flex flex-col overflow-hidden">
      {/* Main Content Layout - Fixed Height with Independent Scrolls */}
      <div className="max-w-7xl mx-auto w-full flex flex-1 min-h-0">
        {/* Report Groups Sidebar/Drawer */}
        <ReportGroupsSidebar {...stableSidebarProps} />

        {/* Report List View */}
        <ReportListView {...reportListViewProps} />
      </div>

      {/* Footer - normal flexbox child, no position:fixed */}
      <ReportFooter
          activeTab={activeTab}
          paginatedPeriods={paginatedPeriods}
          paginatedGroupPeriods={paginatedGroupPeriods}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onPageChange={handlePageChange}
          isLoading={loading}
        />
    </div>
  );
};

export default Index;
